import React, { useState, useEffect } from 'react';
import {
  Card, CardContent, Typography, Box, Divider, List, ListItem,
  ListItemText, Chip, CircularProgress, Stack
} from '@mui/material';
import {
  Email as EmailIcon, Phone as PhoneIcon, LocationCity as CityIcon,
  Business as CompanyIcon, Person as PersonIcon, Wc as GenderIcon,
  Notifications as SubIcon
} from '@mui/icons-material';
import api from '../../services/api';

const DetailRow = ({ icon: Icon, label, value }) => {
  if (!value && value !== false && value !== 0) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8 }}>
      {Icon && <Icon fontSize="small" color="action" />}
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2">{String(value)}</Typography>
      </Box>
    </Box>
  );
};

const ContactDetail = ({ contact, projectId }) => {
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  useEffect(() => {
    if (!contact?._id) {
      setActivities([]);
      return;
    }

    const fetchActivities = async () => {
      setLoadingActivities(true);
      try {
        const res = await api.get('/activities', { params: { contact: contact._id } });
        setActivities(res.data || []);
      } catch (err) {
        console.error('Error fetching activities for contact:', err);
      } finally {
        setLoadingActivities(false);
      }
    };
    fetchActivities();
  }, [contact?._id]);

  if (!contact) {
    return (
      <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Select a contact to view details</Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', overflowY: 'auto', maxHeight: '80vh' }}>
      <CardContent>
        {/* Header */}
        <Typography variant="h5" component="h2" gutterBottom>
          {contact.first_name || ''} {contact.last_name || ''}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`ID: ${contact.contactId || 'Auto'}`} size="small" variant="outlined" />
          <Chip
            label={contact.status === 'active' ? 'Active' : 'Inactive'}
            size="small"
            color={contact.status === 'active' ? 'success' : 'default'}
          />
          {contact.subscribed !== undefined && (
            <Chip
              label={contact.subscribed ? 'Subscribed' : 'Unsubscribed'}
              size="small"
              color={contact.subscribed ? 'info' : 'default'}
              variant="outlined"
            />
          )}
        </Stack>

        <Divider />

        {/* Core Contact Fields */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Contact Information
          </Typography>
          <DetailRow icon={EmailIcon} label="Email" value={contact.email} />
          <DetailRow icon={PhoneIcon} label="Phone" value={contact.phone_number} />
          <DetailRow icon={CityIcon} label="City" value={contact.city} />
          <DetailRow icon={CompanyIcon} label="Company" value={contact.company} />
          <DetailRow icon={GenderIcon} label="Gender" value={contact.gender} />
        </Box>

        {/* Groups */}
        {contact.groups && contact.groups.length > 0 && (
          <>
            <Divider />
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Groups
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {contact.groups.map((g, i) => (
                  <Chip
                    key={typeof g === 'object' ? g._id : i}
                    label={typeof g === 'object' ? g.name : g}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}

        {/* Custom Properties */}
        {contact.properties && Object.keys(contact.properties).length > 0 && (
          <>
            <Divider />
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Custom Properties
              </Typography>
              <List dense disablePadding>
                {Object.entries(contact.properties).map(([key, value]) => (
                  <ListItem key={key} disablePadding sx={{ py: 0.3 }}>
                    <ListItemText
                      primary={<Typography variant="caption" color="text.secondary">{key}</Typography>}
                      secondary={<Typography variant="body2">{String(value)}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}

        {/* Recent Activity */}
        <Divider />
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Recent Activity
          </Typography>
          {loadingActivities ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activities.length > 0 ? (
            <List dense disablePadding>
              {activities.slice(0, 15).map((activity) => (
                <ListItem key={activity._id} disablePadding sx={{ py: 0.3 }}>
                  <ListItemText
                    primary={activity.name || activity.activity_type || 'Event'}
                    secondary={
                      (activity.properties?.path || activity.properties?.url || '') +
                      ' — ' +
                      new Date(activity.created).toLocaleString()
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No recent activity recorded.
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ContactDetail;
